import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import NoResults from "../noResults/noResults";
import Loading from "../loading/Loading";
import Pagination from "../pagination/Pagination";
import { getCustomerCategoriesByPaginationService } from "../../services/categories/services.js";
import { useTranslator } from "../../context/translator";
import ClientCategoryModal from "../clientCategoryModal/ClientCategoryModal.jsx";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";

const ClientCategoriesTable = () => {
  const { user } = useSelector((state) => state.user);

  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );

  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [pageNumber, setPageNumber] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState("");
  const { translate } = useTranslator();
  const [newCategory] = useState({
    _id: "new",
    key: "",
    name: "",
    counter: 0,
    active: true,
    isDisabledEmail: false,
    isDisabledPhoneNumber: false,
    isSpecialCommunication: false,
  });

  const handleOnChangeSearch = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const getCategories = React.useCallback(async () => {
    setIsLoading(true);

    const data = await getCustomerCategoriesByPaginationService({
      companyId: company?._id,
      search,
      page,
      pageSize: 5,
    });

    setIsLoading(false);
    setCategories(data?.categories ?? []);
    const count = data?.totalRecords ?? 0;
    setTotalRecords(count);
    setPageNumber(Math.ceil(count / 5));
  }, [company?._id, page, search]);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  if (userLoading || companyLoading) return null;

  return (
    <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
      {/* Search + New */}
      <div className="grid gap-6 mb-6 md:grid-cols-2 mt-2 mr-2 ml-2">
        <div>
          <label
            htmlFor="search"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            {translate("search")}
          </label>
          <input
            type="text"
            id="search"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            placeholder={translate("search") + "..."}
            onChange={handleOnChangeSearch}
          />
        </div>

        <div className="flex justify-end items-end">
          <ClientCategoryModal
            key={"new"}
            category={newCategory}
            getCategories={getCategories}
            text={translate("new-category")}
            cssButton="text-white bg-gradient-to-r from-green-400 via-green-500 to-green-600 hover:bg-gradient-to-br font-medium rounded-lg text-sm px-5 py-2.5 text-center"
          />
        </div>
      </div>

      {!isLoading && categories.length === 0 && (
        <NoResults message="No category found" />
      )}
      {isLoading && categories.length === 0 && <Loading />}

      {categories.length > 0 && (
        <>
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead className="text-xs bg-gray-50 dark:bg-gray-700 uppercase text-gray-700 dark:text-gray-400">
              <tr>
                <th className="px-6 py-3">{translate("key")}</th>
                <th className="px-6 py-3">{translate("name")}</th>
                <th className="px-6 py-3">{translate("counter")}</th>
                <th className="px-6 py-3">{translate("status")}</th>
                <th className="px-6 py-3">
                  <span className="sr-only">{translate("edit")}</span>
                </th>
              </tr>
            </thead>

            <tbody>
              {categories.map((cat) => (
                <tr
                  key={cat._id}
                  className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  <td className="px-6 py-4 font-medium">{cat.key}</td>
                  <td className="px-6 py-4 font-medium">{cat.name}</td>
                  <td className="px-6 py-4 font-medium">{cat.counter}</td>
                  <td className="px-6 py-4 font-medium">
                    {cat.active ? translate("active") : translate("deactivate")}
                  </td>

                  <td className="px-6 py-4 text-right">
                    <ClientCategoryModal
                      key={cat._id}
                      category={cat}
                      getCategories={getCategories}
                      text={translate("edit")}
                      cssButton="font-medium text-blue-600 dark:text-blue-500 hover:underline"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!isLoading && (
            <Pagination
              key={"paginationClientCategories"}
              data={categories}
              page={page}
              pageNumber={pageNumber}
              isLoading={isLoading}
              setPage={setPage}
              totalRecords={totalRecords}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ClientCategoriesTable;
